import React, { useState, useEffect } from 'react';
import { Plus, Trash2, LayoutDashboard, Wrench } from 'lucide-react';
import Navbar from '../../layout/Navbar';
import { Link, useNavigate } from 'react-router-dom';
import { testDataApi } from '../../../api/testdata';

const TestDataPage = () => {
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchFixtures();
  }, []);

  const fetchFixtures = async () => {
    try {
      setLoading(true);
      const data = await testDataApi.getAllFixtures();
      
      // Transform the data to match our component's expected format
      const formattedFixtures = data.map(fixture => ({
        id: fixture.id,
        name: fixture.name,
        description: fixture.description,
        dataCount: Object.keys(fixture.variables).length,
        updatedAt: new Date(fixture.updated_at).toLocaleDateString()
      }));
      
      setFixtures(formattedFixtures);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch fixtures:', err);
      setError('Failed to load fixtures. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleFixtureClick = (fixtureId) => {
    navigate(`/testdata/${fixtureId}`);
  };

  const handleDeleteFixture = async (event, fixtureId) => {
    event.stopPropagation();
    
    if (isDeleting) return;
    
    if (!window.confirm('Are you sure you want to delete this fixture? This action cannot be undone.')) {
      return;
    }
    
    try {
      setIsDeleting(true);
      setDeleteError(null);
      
      await testDataApi.deleteFixture(fixtureId);
      setFixtures(fixtures.filter(fixture => fixture.id !== fixtureId));
      
    } catch (err) {
      console.error('Failed to delete fixture:', err);
      setDeleteError('Failed to delete fixture. Please try again later.');
    } finally {
      setIsDeleting(false);
    }
  };

  const renderFixturesContent = () => (
    <>
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {deleteError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
          {deleteError}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : fixtures.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {fixtures.map((fixture) => (
            <div
              key={fixture.id}
              className="bg-white rounded-lg shadow border border-gray-200 cursor-pointer hover:shadow-md transition-shadow relative"
              onClick={() => handleFixtureClick(fixture.id)}
            >
              <button
                className="absolute top-3 right-3 p-1.5 text-gray-500 hover:text-red-600 hover:bg-gray-100 rounded-md transition-colors"
                onClick={(e) => handleDeleteFixture(e, fixture.id)}
                title="Delete fixture"
                disabled={isDeleting}
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <div className="p-4 border-b">
                <h3 className="text-lg font-semibold">{fixture.name}</h3>
                <p className="text-sm text-gray-600">{fixture.description}</p>
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Data Items</span>
                  <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded">{fixture.dataCount}</span>
                </div>
                <div className="text-xs text-gray-500 pt-4 mt-4 border-t">Updated {fixture.updatedAt}</div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <Plus className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No fixtures yet</h3>
          <p className="text-gray-600 mb-4">Create your first data fixture to get started</p>
          <Link 
            to="/testdata/create"
            className="px-4 py-2 bg-blue-600 text-white rounded-md"
          >
            Create Fixture
          </Link>
        </div>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="flex">
        <aside className="hidden md:flex flex-col w-64 border-r bg-gray-100/40 p-4 gap-4 min-h-[calc(100vh-64px)]">
          <nav className="grid gap-2">
            <Link
              to="/testdata"
              className="flex items-center justify-start px-4 py-2 rounded-md bg-gray-200"
            >
              <LayoutDashboard className="w-4 h-4 mr-2" />
              Data Fixtures
            </Link>
            <Link
              to="/testdata/cleanup"
              className="flex items-center justify-start px-4 py-2 rounded-md hover:bg-gray-200"
            >
              <Wrench className="w-4 h-4 mr-2" />
              Data Cleanup
            </Link>
          </nav>
        </aside>
        
        <main className="flex-1 p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold">Data Fixtures</h1>
              <p className="text-gray-600">Manage reusable test data for your test suites</p>
            </div>
            <Link 
              to="/testdata/create"
              className="px-4 py-2 bg-blue-600 text-white rounded-md flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Create Fixture
            </Link>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {renderFixturesContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default TestDataPage; 